/**
 * Kopfaktion „Privat schalten"/„Privat aufheben" (`object-private`, Spec 05b,
 * „Umschalten"). Fünfter Knopf in derselben Kopfzeile wie `object-access`
 * (`routes/ObjectDetail.tsx`) — kein sechster Reiter (ADR 0011).
 *
 * Der Ausweg bei 409 `would_lock_out` braucht `AccessAddGrantSheet`
 * (`features/access`), das `features/objects` nicht kennen darf
 * (`apps/web/CLAUDE.md`, „features kennen einander nicht"): Diese Komponente
 * bleibt bei der Bestätigung selbst — inklusive des zweiten Streifentexts mit
 * dem Servertext — und reicht nur den letzten Schritt über
 * `onRequestGrantSelf` an `ObjectDetail` weiter, das dort schon `AccessSheet`
 * komponiert.
 */

import { useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { FIELD_CLASS } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useSwitchVisibility } from '@/features/objects/api'

type NextVisibility = 'private' | 'inherited'

type StripState =
  | { readonly kind: 'confirm'; readonly next: NextVisibility }
  | { readonly kind: 'lockout'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string }

interface PrivateToggleProps {
  readonly entityId: string
  readonly label: string
  /** Unmittelbares Elternobjekt, leer an einer Wurzel — dieselbe Herkunft wie
   * `duplicateParentLabel` in `ObjectDetail.tsx` (`ObjectHead.path`, letztes
   * Glied). */
  readonly parentLabel: string
  readonly version: number
  /** `ObjectHead.visibility.value` — `'private'` oder `'inherited'`. */
  readonly visibility: string
  /** 409 `would_lock_out`: Der Streifen bleibt offen und trägt einen zweiten
   * Knopf, „Mir Zugriff geben" — das Öffnen des Sheets liegt beim Aufrufer. */
  readonly onRequestGrantSelf: () => void
}

/**
 * Der Rückfragetext (Spec 05b, „Umschalten (`object-private`)"), wörtlich.
 * Ohne Elternobjekt entfällt der Satz, der es nennt — an einer Wurzel ist
 * `private` ohnehin wirkungslos (Spec, „Was „privat" bedeutet — und was
 * nicht"), und „Aufheben" ohne Elternobjekt hätte niemanden, auf den es sich
 * bezöge.
 */
function buildConfirmMessage(next: NextVisibility, label: string, parentLabel: string): string {
  if (next === 'private') {
    const base = `${label} privat schalten? Danach sieht den Inhalt nur noch, wer einen Zugriff direkt auf ${label} oder darunter hat.`
    return parentLabel === '' ? base : `${base} Wer Zugriff auf ${parentLabel} hat, sieht den Ordner weiterhin, aber nichts darin.`
  }
  return parentLabel === ''
    ? `Privat für ${label} aufheben?`
    : `Privat für ${label} aufheben? Danach sieht den Inhalt wieder jeder, der Zugriff auf ${parentLabel} hat.`
}

export function PrivateToggle({ entityId, label, parentLabel, version, visibility, onRequestGrantSelf }: PrivateToggleProps) {
  const switchMutation = useSwitchVisibility()
  const [strip, setStrip] = useState<StripState | null>(null)

  const next: NextVisibility = visibility === 'private' ? 'inherited' : 'private'

  async function handleConfirm(target: NextVisibility) {
    try {
      await switchMutation.mutateAsync({ entityId, visibility: target, ifMatch: String(version) })
      setStrip(null)
    } catch (error) {
      if (error instanceof ApiError && error.code === 'would_lock_out') {
        setStrip({ kind: 'lockout', message: error.message })
      } else {
        setStrip({
          kind: 'error',
          message: error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.',
        })
      }
    }
  }

  const message =
    strip === null ? null : strip.kind === 'confirm' ? buildConfirmMessage(strip.next, label, parentLabel) : strip.message

  const confirm =
    strip?.kind === 'confirm'
      ? {
          confirmLabel: strip.next === 'private' ? 'Privat schalten' : 'Privat aufheben',
          confirmTestId: 'object-private-confirm',
          cancelLabel: 'Abbrechen',
          cancelTestId: 'object-private-cancel',
          onConfirm: () => void handleConfirm(strip.next),
        }
      : strip?.kind === 'lockout'
        ? {
            confirmLabel: 'Mir Zugriff geben',
            confirmTestId: 'object-private-grant-self',
            cancelLabel: 'Abbrechen',
            cancelTestId: 'object-private-cancel',
            onConfirm: () => {
              setStrip(null)
              onRequestGrantSelf()
            },
          }
        : undefined

  return (
    <>
      <button
        type="button"
        data-testid="object-private"
        onClick={() => {
          setStrip({ kind: 'confirm', next })
        }}
        className={`text-text px-3 ${FIELD_CLASS}`}
      >
        {visibility === 'private' ? 'Privat aufheben' : 'Privat schalten'}
      </button>

      {/* `confirm` per Spread statt als Prop mit `undefined`: `ConfirmationStripProps.confirm`
       * ist optional (`confirm?: ConfirmationStripConfirm`), nicht `| undefined` — unter
       * `exactOptionalPropertyTypes: true` (TS2375) muss die Eigenschaft fehlen, nicht auf
       * `undefined` stehen. Die Komponente selbst bleibt unverändert (Bestand aus 05a, von
       * `AccessSheet.tsx`, `Today.tsx`, `Geld.tsx`, `TenantLedger.tsx` mitbenutzt). */}
      <ConfirmationStrip
        testId="object-private-confirmation"
        message={message}
        onDismiss={() => {
          setStrip(null)
        }}
        {...(confirm !== undefined ? { confirm } : {})}
      />
    </>
  )
}
